import { vnTemplate } from '@valnet/vnKnockoutJsTemplates/vnTemplate';
import { html, LpButton, LpDatePicker, LpDatePickerCalendarToolbar, LpDatePickerMonthYearPanel, LpDatePickerRangeHeader, LpDatePickerTrigger, } from '~/design-system';
import { formatCalendarMonthLabel, formatPickerLabel, formatTypedDateInput, formatTypedDateValue, getAdjacentCalendarMonth, getCalendarMonthDays, getDateString, isSameCalendarDay, normalizeDateOnly, parsePartialTypedDate, parseTypedDate, } from '~/utils/datePicker';
/**
 * App-owned date filter so LeadPilot calendar UI ships with the app,
 * without depending on local/unreleased valnet_jslib vendor edits.
 */
export class filterDate_template extends vnTemplate {
    constructor() {
        super(...arguments);
        this.calendarInitFrame = null;
        this.calendarInitCancelled = false;
        this.outsideClickListener = null;
        this.escapeKeyListener = null;
    }
    getTemplateHtml() {
        const panelHtml = html `
                    ${LpDatePickerRangeHeader({
            startAttributes: {
                'data-bind': "text: pickerStartLabel, css: { 'is-selected': pickerStartLabel() !== '—' }",
            },
            endAttributes: {
                'data-bind': "text: pickerEndLabel, css: { 'is-selected': pickerStartLabel() !== '—' }",
            },
        })}

                    <div class="time-period-panel time-period-panel--split">
                        <div class="time-period-body period is-selected" data-bind="visible: selectedFilter() == 'dates'">
                            <div class="time-period-calendars time-period-calendars--split">
                                ${this.getCalendarPanelHtml("start", "Start Date", "calendarStartMonthLabel", "calendarStartYears", "calendarStartDays")}
                                ${this.getCalendarPanelHtml("end", "End Date", "calendarEndMonthLabel", "calendarEndYears", "calendarEndDays")}
                            </div>
                        </div>

                    </div>

                    <div class="time-period-footer">
                        ${LpButton({
            label: "Clear",
            size: "sm",
            variant: "ghost",
            className: "time-period-footer__clear",
            attributes: {
                "data-bind": "click: onPickerClear",
            },
        })}
                        <div class="time-period-footer__actions">
                            ${LpButton({
            label: "Cancel",
            size: "sm",
            variant: "secondary",
            attributes: {
                "data-bind": "click: onClickCancel",
            },
        })}
                            ${LpButton({
            label: "Apply",
            size: "sm",
            variant: "primary",
            attributes: {
                "data-bind": "click: apply",
            },
        })}
                        </div>
                    </div>
            `;
        return String(LpDatePicker({
            trigger: LpDatePickerTrigger({
                startLabel: "",
                endLabel: "",
                startInputAttributes: {
                    "data-bind": "value: typedStartDate, valueUpdate: 'input', event: { input: function(data, event) { onTypedDateInput('start', event); }, keydown: function(data, event) { return onTypedDateKeydown('start', event); }, focus: function(data, event) { onTypedDateFocus('start', event); }, blur: function() { onTypedDateBlur('start'); }, wheel: function(data, event) { return onTypedDateWheel('start', event); } }",
                    "data-lp-date-input": "start",
                    autocomplete: "off",
                    inputmode: "numeric",
                    maxlength: 10,
                },
                endInputAttributes: {
                    "data-bind": "value: typedEndDate, valueUpdate: 'input', event: { input: function(data, event) { onTypedDateInput('end', event); }, keydown: function(data, event) { return onTypedDateKeydown('end', event); }, focus: function(data, event) { onTypedDateFocus('end', event); }, blur: function() { onTypedDateBlur('end'); }, wheel: function(data, event) { return onTypedDateWheel('end', event); } }",
                    "data-lp-date-input": "end",
                    autocomplete: "off",
                    inputmode: "numeric",
                    maxlength: 10,
                },
                toggleAttributes: {
                    "data-bind": "click: toggle, attr: { 'aria-expanded': isShown() ? 'true' : 'false' }",
                },
            }),
            panel: panelHtml,
            panelClassName: "time-period-panel--dual",
            attributes: {
                "data-bind": "css: { 'is-open': isShown, 'is-applied': !isActive() }",
            },
            panelAttributes: {
                "data-bind": "visible: isShown",
            },
        }));
    }
    getTemplateName() {
        return "lp-filterDate-template";
    }
    getCalendarPanelHtml(part, title, monthLabelBinding, yearsBinding, daysBinding) {
        return html `
            <section class="time-period-calendar time-period-calendar--${part}" aria-label="${title}">
                <h4 class="time-period-calendar__title">${title}</h4>
                ${LpDatePickerCalendarToolbar({
            previousAttributes: {
                'data-bind': `click: function (data, event) { return onCalendarPrevMonth('${part}', event); }`,
            },
            headingButtonAttributes: {
                'data-bind': `click: function (data, event) { return toggleCalendarMonthYearPicker('${part}', event); }, attr: { 'aria-expanded': showMonthYearPicker() && activeCalendarPart() === '${part}' ? 'true' : 'false' }`,
            },
            headingLabelAttributes: {
                'data-bind': `text: ${monthLabelBinding}`,
            },
            nextAttributes: {
                'data-bind': `click: function (data, event) { return onCalendarNextMonth('${part}', event); }`,
            },
        })}
                ${LpDatePickerMonthYearPanel({
            attributes: {
                'data-bind': `visible: showMonthYearPicker() && activeCalendarPart() === '${part}', click: function () { return false; }`,
            },
            monthSectionAttributes: {
                'data-bind': "visible: monthYearPickerStep() === 'month'",
            },
            yearSectionAttributes: {
                'data-bind': "visible: monthYearPickerStep() === 'year'",
            },
            yearsBinding,
            monthButtonAttributes: {
                'data-bind': `text: shortLabel, click: function (month, event) { $parent.onCalendarMonthSelect('${part}', value, event); }, css: { 'is-active': value === $parent.${part}CalendarViewDate().getMonth() }`,
            },
            yearButtonAttributes: {
                'data-bind': `text: $data, click: function (year, event) { $parent.onCalendarYearSelect('${part}', year, event); }, css: { 'is-active': $data === $parent.${part}CalendarViewDate().getFullYear() }`,
            },
        })}
                <div class="time-period-calendar__grid" data-bind="visible: !showMonthYearPicker() || activeCalendarPart() !== '${part}'">
                    <div class="lp-date-picker_weekdays" data-bind="foreach: calendarWeekdays">
                        <span data-bind="text: $data"></span>
                    </div>
                    <div class="lp-date-picker_days" data-bind="foreach: ${daysBinding}">
                        <button
                            type="button"
                            class="lp-date-picker_day"
                            data-bind="text: label, click: function (day, event) { return $parent.onCalendarDayClick('${part}', day, event); }, css: $parent.calendarDayCss($data), attr: { 'aria-label': ariaLabel }"
                        ></button>
                    </div>
                </div>
            </section>
        `;
    }
    getMainElementClasses() {
        return [];
    }
    buildViewModel() {
        // define the viewmodel data/ functions
        let huit_neuf_this = this;
        let currentDate = new Date();
        const pickerStartLabel = ko.observable("—");
        const pickerEndLabel = ko.observable("—");
        const label = ko.observable("Date Range");
        const startDate = ko.observable(this.getDateString(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1), true));
        const endDate = ko.observable(this.getDateString(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59), true));
        const typedStartDate = ko.observable(this.formatTypedDateValue(startDate()));
        const typedEndDate = ko.observable(this.formatTypedDateValue(endDate()));
        const startCalendarViewDate = ko.observable(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
        const endCalendarViewDate = ko.observable(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
        const showMonthYearPicker = ko.observable(false);
        const monthYearPickerStep = ko.observable("month");
        const activeCalendarPart = ko.observable("start");
        return { startDate: startDate,
            lastStartDate: ko.observable(null),
            endDate: endDate,
            lastEndDate: ko.observable(null),
            months: ko.computed(function () { return huit_neuf_this.getAllMonths(); }, this),
            idStartPicker: ko.observable("startDatePicker"),
            idEndPicker: ko.observable("endDatePicker"),
            showClearDatesButton: ko.observable(false),
            isActive: ko.observable(true),
            isShown: ko.observable(false),
            selectedFilter: ko.observable("dates"),
            lastSelectedFilter: ko.observable("dates"),
            fromRange: ko.observable(null),
            lastFromRange: ko.observable(null),
            toRange: ko.observable(null),
            lastToRange: ko.observable(null),
            selectedMonth: ko.observable(null),
            lastSelectedMonth: ko.observable(null),
            label: label,
            hmsEnabled: ko.observable(false),
            pickerStartLabel: pickerStartLabel,
            pickerEndLabel: pickerEndLabel,
            typedStartDate: typedStartDate,
            typedEndDate: typedEndDate,
            calendarViewDate: startCalendarViewDate,
            startCalendarViewDate: startCalendarViewDate,
            endCalendarViewDate: endCalendarViewDate,
            showMonthYearPicker: showMonthYearPicker,
            monthYearPickerStep: monthYearPickerStep,
            activeCalendarPart: activeCalendarPart,
            calendarWeekdays: ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"],
            calendarMonths: this.getCalendarMonths(),
            calendarYears: ko.computed(function () { return huit_neuf_this.getCalendarYears(startCalendarViewDate()); }, this),
            calendarMonthLabel: ko.computed(function () { return huit_neuf_this.formatCalendarMonthLabel(startCalendarViewDate()); }, this),
            calendarStartMonthLabel: ko.computed(function () { return huit_neuf_this.formatCalendarMonthLabel(startCalendarViewDate()); }, this),
            calendarEndMonthLabel: ko.computed(function () { return huit_neuf_this.formatCalendarMonthLabel(endCalendarViewDate()); }, this),
            calendarStartYears: ko.computed(function () { return huit_neuf_this.getCalendarYears(startCalendarViewDate()); }, this),
            calendarEndYears: ko.computed(function () { return huit_neuf_this.getCalendarYears(endCalendarViewDate()); }, this),
            calendarStartDays: ko.computed(function () { return huit_neuf_this.getCalendarMonthDays(startCalendarViewDate()); }, this),
            calendarEndDays: ko.computed(function () { return huit_neuf_this.getCalendarMonthDays(endCalendarViewDate()); }, this),
            onClickMonthAction: function (data, event) { huit_neuf_this.onClickMonthAction(); },
            onClickClear: function (data, event) { huit_neuf_this.onClickClear(); },
            onPickerClear: function () { huit_neuf_this.onPickerClear(); },
            onClickCancel: function (data, event) { huit_neuf_this.onClickCancel(); },
            onClickEdit: function (data, event) { huit_neuf_this.onClickEdit(); },
            apply: function (data, event) { huit_neuf_this.apply(); },
            toggle: function (data, event) { huit_neuf_this.toggle(); },
            onTypedDateInput: function (part, event) { huit_neuf_this.onTypedDateInput(part, event); },
            onTypedDateKeydown: function (part, event) { return huit_neuf_this.onTypedDateKeydown(part, event); },
            onTypedDateFocus: function (part, event) { huit_neuf_this.onTypedDateFocus(part, event); },
            onTypedDateBlur: function (part) { huit_neuf_this.onTypedDateBlur(part); },
            onTypedDateWheel: function (part, event) { return huit_neuf_this.onTypedDateWheel(part, event); },
            toggleCalendarMonthYearPicker: function (part, event) { return huit_neuf_this.toggleCalendarMonthYearPicker(part, event); },
            onCalendarMonthSelect: function (part, month, event) { return huit_neuf_this.onCalendarMonthSelect(part, month, event); },
            onCalendarYearSelect: function (part, year, event) { return huit_neuf_this.onCalendarYearSelect(part, year, event); },
            onCalendarPrevMonth: function (part, event) { return huit_neuf_this.onCalendarNavClick(part, -1, event); },
            onCalendarNextMonth: function (part, event) { return huit_neuf_this.onCalendarNavClick(part, 1, event); },
            onCalendarDayClick: function (part, day, event) { return huit_neuf_this.onCalendarDayClick(part, day, event); },
            calendarDayCss: function (day) { return huit_neuf_this.getCalendarDayCss(day); }
        };
    }
    onClickMonthAction() {
        let data = this.getTemplateViewModel().selectedMonth();
        let startDate = new Date(data.currentYear, data.currentMonth - 1, 1, 0, 0, 0);
        let endDate = new Date(data.currentYear, data.currentMonth, 0, 23, 59, 59);
        let startDateString = this.getDateString(startDate, this.getTemplateViewModel().hmsEnabled());
        let endDateString = this.getDateString(endDate, this.getTemplateViewModel().hmsEnabled());
        //set filters
        this.viewModel[this.idElement].startDate(startDateString);
        this.viewModel[this.idElement].endDate(endDateString);
        //set calender
        this.setCalenderDates(startDate, endDate);
        this.updatePickerLabels();
        this.getTemplateViewModel().selectedMonth(null);
    }
    toggle() {
        const vm = this.getTemplateViewModel();
        vm.isShown(true);
        this.syncTypedDateInputs();
        if (vm.startDate() || vm.endDate()) {
            this.setCalenderDates(vm.startDate(), vm.endDate());
        }
        this.bindPickerDismiss();
        requestAnimationFrame(() => {
            this.refreshCalendarRangeClasses();
            this.updatePickerLabels();
        });
    }
    getPickerRoot() {
        return document.getElementById(this.idElement);
    }
    bindPickerDismiss() {
        this.unbindPickerDismiss();
        this.outsideClickListener = (event) => {
            if (!this.getTemplateViewModel().isShown()) {
                return;
            }
            const root = this.getPickerRoot();
            if (!root || !(event.target instanceof Node) || root.contains(event.target)) {
                return;
            }
            this.onClickCancel();
        };
        this.escapeKeyListener = (event) => {
            if (event.key !== "Escape" || !this.getTemplateViewModel().isShown()) {
                return;
            }
            this.onClickCancel();
        };
        requestAnimationFrame(() => {
            if (this.outsideClickListener) {
                document.addEventListener("click", this.outsideClickListener);
            }
            if (this.escapeKeyListener) {
                document.addEventListener("keydown", this.escapeKeyListener);
            }
        });
    }
    unbindPickerDismiss() {
        if (this.outsideClickListener) {
            document.removeEventListener("click", this.outsideClickListener);
            this.outsideClickListener = null;
        }
        if (this.escapeKeyListener) {
            document.removeEventListener("keydown", this.escapeKeyListener);
            this.escapeKeyListener = null;
        }
    }
    cancelCalendarInit() {
        this.calendarInitCancelled = true;
        if (this.calendarInitFrame !== null) {
            cancelAnimationFrame(this.calendarInitFrame);
            this.calendarInitFrame = null;
        }
    }
    destroyCalendars() {
        this.cancelCalendarInit();
        this.unbindPickerDismiss();
    }
    buildCalendarWhenReady() {
        this.cancelCalendarInit();
        this.calendarInitFrame = requestAnimationFrame(() => {
            this.calendarInitFrame = null;
            this.buildCalendar();
        });
    }
    buildCalendar() {
        this.setCalendarViewDateFromRange(this.getTemplateViewModel().startDate(), this.getTemplateViewModel().endDate());
        this.updatePickerLabels();
    }
    setCalenderDates(startDate, endDate) {
        this.setCalendarViewDateFromRange(startDate, endDate);
        requestAnimationFrame(() => {
            this.refreshCalendarRangeClasses();
            this.updatePickerLabels();
        });
    }
    getAdjacentCalendarMonth(date, offset) {
        return getAdjacentCalendarMonth(date, offset);
    }
    formatCalendarMonthLabel(date) {
        return formatCalendarMonthLabel(date);
    }
    getCalendarMonthDays(monthDate) {
        return getCalendarMonthDays(monthDate);
    }
    getCalendarDayCss(day) {
        const typedDates = this.getTypedDateRange(false);
        const date = this.parseTypedDate(day.dateValue, true);
        const startDate = typedDates.startDate;
        const endDate = typedDates.endDate;
        const isRangeStart = Boolean(date && startDate && this.isSameCalendarDay(date, startDate));
        const isRangeEnd = Boolean(date && endDate && this.isSameCalendarDay(date, endDate));
        const isInRange = Boolean(date && startDate && endDate && date > startDate && date < endDate);
        return {
            "is-muted": !day.isCurrentMonth,
            "is-today": day.isToday,
            "is-range-start": isRangeStart,
            "is-range-end": isRangeEnd,
            "is-in-range": isInRange,
        };
    }
    getCalendarMonths() {
        return [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ].map((shortLabel, value) => ({ value, shortLabel }));
    }
    getCalendarYears(viewDate) {
        const currentYear = new Date().getFullYear();
        const viewYear = viewDate.getFullYear();
        const startYear = Math.min(viewYear, currentYear - 25);
        const endYear = Math.max(viewYear, currentYear + 5);
        return Array.from({ length: endYear - startYear + 1 }, (_, index) => startYear + index);
    }
    toggleCalendarMonthYearPicker(part, event) {
        event?.preventDefault();
        event?.stopPropagation();
        const vm = this.getTemplateViewModel();
        const shouldShow = vm.activeCalendarPart() !== part || !vm.showMonthYearPicker();
        vm.activeCalendarPart(part);
        vm.showMonthYearPicker(shouldShow);
        if (shouldShow) {
            vm.monthYearPickerStep("month");
        }
        return false;
    }
    onCalendarMonthSelect(part, month, event) {
        event?.preventDefault();
        event?.stopPropagation();
        const vm = this.getTemplateViewModel();
        const viewDate = this.getCalendarViewDate(part);
        this.setCalendarViewDate(part, new Date(viewDate.getFullYear(), Number(month), 1));
        vm.monthYearPickerStep("year");
        return false;
    }
    onCalendarYearSelect(part, year, event) {
        event?.preventDefault();
        event?.stopPropagation();
        const vm = this.getTemplateViewModel();
        const viewDate = this.getCalendarViewDate(part);
        this.setCalendarViewDate(part, new Date(Number(year), viewDate.getMonth(), 1));
        vm.monthYearPickerStep("month");
        vm.showMonthYearPicker(false);
        return false;
    }
    onCalendarNavClick(part, offset, event) {
        event?.preventDefault();
        event?.stopPropagation();
        this.getTemplateViewModel().monthYearPickerStep("month");
        this.getTemplateViewModel().showMonthYearPicker(false);
        this.shiftCalendarMonth(part, offset);
        return false;
    }
    onCalendarDayClick(part, day, event) {
        event?.preventDefault();
        event?.stopPropagation();
        const pickedStartDate = this.parseTypedDate(day.dateValue, true);
        const pickedEndDate = this.parseTypedDate(day.dateValue, false);
        const typedDates = this.getTypedDateRange(false);
        if (!pickedStartDate || !pickedEndDate) {
            return false;
        }
        if (part === "start") {
            this.setTypedDateValue("start", this.formatTypedDateValue(pickedStartDate));
            if (typedDates.endDate && pickedStartDate > typedDates.endDate) {
                this.setTypedDateValue("end", this.formatTypedDateValue(pickedEndDate));
            }
        }
        else {
            if (typedDates.startDate && pickedEndDate < typedDates.startDate) {
                this.setTypedDateValue("start", this.formatTypedDateValue(pickedStartDate));
            }
            this.setTypedDateValue("end", this.formatTypedDateValue(pickedEndDate));
        }
        this.syncTypedDateDomInputs();
        this.previewTypedDateSelection();
        this.updatePickerLabels();
        return false;
    }
    shiftCalendarMonth(part, offset) {
        this.setCalendarViewDate(part, this.getAdjacentCalendarMonth(this.getCalendarViewDate(part), offset));
    }
    getCalendarViewDate(part) {
        const vm = this.getTemplateViewModel();
        return part === "start" ? vm.startCalendarViewDate() : vm.endCalendarViewDate();
    }
    setCalendarViewDate(part, date) {
        const vm = this.getTemplateViewModel();
        if (part === "start") {
            vm.startCalendarViewDate(date);
        }
        else {
            vm.endCalendarViewDate(date);
        }
    }
    getCalendarMonthStart(date) {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    }
    setCalendarViewDateFromRange(startDate, endDate) {
        const vm = this.getTemplateViewModel();
        const startAnchorDate = this.normalizeDateOnly(startDate) || this.normalizeDateOnly(endDate) || new Date();
        const endAnchorDate = this.normalizeDateOnly(endDate) || this.getAdjacentCalendarMonth(startAnchorDate, 1);
        vm.startCalendarViewDate(this.getCalendarMonthStart(startAnchorDate));
        vm.endCalendarViewDate(this.getCalendarMonthStart(endAnchorDate));
    }
    onTypedDateInput(part, event) {
        const vm = this.getTemplateViewModel();
        vm.selectedFilter("dates");
        const input = event?.target instanceof HTMLInputElement ? event.target : null;
        const currentValue = input ? input.value : this.getTypedDateValue(part);
        const formattedValue = this.formatTypedDateInput(currentValue);
        this.setTypedDateValue(part, formattedValue);
        if (input && input.value !== formattedValue) {
            input.value = formattedValue;
            input.setSelectionRange(formattedValue.length, formattedValue.length);
        }
        if (part === "start" && formattedValue.length === 10) {
            this.focusTypedDateInput("end");
        }
        if (!this.canPreviewTypedDates()) {
            return;
        }
        const typedDates = this.getTypedDateRange(false);
        if (typedDates.invalid) {
            return;
        }
        this.setCalenderDates(typedDates.startDate, typedDates.endDate);
    }
    onTypedDateKeydown(part, event) {
        if (event.key === "Enter") {
            event.preventDefault();
            this.applyTypedDates(true);
            return false;
        }
        if (event.key === "Escape") {
            event.preventDefault();
            this.onClickCancel();
            return false;
        }
        if (event.key === "ArrowUp" || event.key === "ArrowDown") {
            event.preventDefault();
            this.shiftTypedDate(part, event.key === "ArrowUp" ? 1 : -1);
            return false;
        }
        return true;
    }
    onTypedDateFocus(part, event) {
        this.openTypedDatePicker();
    }
    onTypedDateBlur(part) {
        const value = this.getTypedDateValue(part);
        if (!value) {
            this.previewTypedDateSelection();
            return;
        }
        const date = this.parsePartialTypedDate(value, part === "start");
        if (date) {
            this.setTypedDateValue(part, this.formatTypedDateValue(date));
        }
        this.previewTypedDateSelection();
    }
    onTypedDateWheel(part, event) {
        if (!this.getTypedDateValue(part)) {
            return true;
        }
        event.preventDefault();
        this.shiftTypedDate(part, event.deltaY < 0 ? 1 : -1);
        return false;
    }
    applyTypedDates(applyFilter = true) {
        const vm = this.getTemplateViewModel();
        this.completeTypedDateInput("start");
        this.completeTypedDateInput("end");
        const typedDates = this.getTypedDateRange(true);
        if (typedDates.invalid) {
            this.showErrorMsg();
            return false;
        }
        if (!typedDates.hasValue) {
            this.onClickClear();
            return true;
        }
        if (typedDates.startDate && typedDates.endDate && typedDates.startDate > typedDates.endDate) {
            this.showErrorMsg();
            return false;
        }
        this.clearValues(false);
        vm.selectedFilter("dates");
        vm.startDate(this.getDateString(typedDates.startDate, vm.hmsEnabled()));
        vm.endDate(this.getDateString(typedDates.endDate, vm.hmsEnabled()));
        vm.typedStartDate(typedDates.startDate ? this.formatTypedDateValue(typedDates.startDate) : "");
        vm.typedEndDate(typedDates.endDate ? this.formatTypedDateValue(typedDates.endDate) : "");
        this.syncTypedDateDomInputs();
        this.setCalenderDates(typedDates.startDate, typedDates.endDate);
        this.refreshCalendarRangeClasses();
        vm.isActive(false);
        vm.isShown(false);
        this.unbindPickerDismiss();
        if (applyFilter) {
            this.applyFilter();
        }
        return true;
    }
    completeTypedDateInput(part) {
        const value = this.getTypedDateValue(part);
        if (!value) {
            return;
        }
        const date = this.parsePartialTypedDate(value, part === "start");
        if (date) {
            this.setTypedDateValue(part, this.formatTypedDateValue(date));
        }
    }
    getTypedDateRange(strict) {
        const vm = this.getTemplateViewModel();
        const startValue = String(vm.typedStartDate() || "").trim();
        const endValue = String(vm.typedEndDate() || "").trim();
        const startDate = this.parseTypedDate(startValue, true);
        const endDate = this.parseTypedDate(endValue, false);
        const hasValue = Boolean(startValue || endValue);
        const invalid = (strict || startValue.length >= 10) && Boolean(startValue && !startDate)
            || (strict || endValue.length >= 10) && Boolean(endValue && !endDate);
        return { startDate, endDate, hasValue, invalid };
    }
    parseTypedDate(value, isStart) {
        return parseTypedDate(value, isStart);
    }
    parsePartialTypedDate(value, isStart) {
        return parsePartialTypedDate(value, isStart);
    }
    formatTypedDateInput(value) {
        return formatTypedDateInput(value);
    }
    getTypedDateValue(part) {
        const vm = this.getTemplateViewModel();
        return String(part === "start" ? vm.typedStartDate() || "" : vm.typedEndDate() || "");
    }
    setTypedDateValue(part, value) {
        const vm = this.getTemplateViewModel();
        if (part === "start") {
            vm.typedStartDate(value);
        }
        else {
            vm.typedEndDate(value);
        }
    }
    syncTypedDateDomInputs() {
        const root = this.getPickerRoot();
        const vm = this.getTemplateViewModel();
        const pickerRoot = root?.querySelector("[data-lp-component='DatePicker']") || root;
        if (!pickerRoot) {
            return;
        }
        const startInput = pickerRoot.querySelector("[data-lp-date-input='start']");
        const endInput = pickerRoot.querySelector("[data-lp-date-input='end']");
        if (startInput && startInput.value !== String(vm.typedStartDate() || "")) {
            startInput.value = String(vm.typedStartDate() || "");
        }
        if (endInput && endInput.value !== String(vm.typedEndDate() || "")) {
            endInput.value = String(vm.typedEndDate() || "");
        }
    }
    canPreviewTypedDates() {
        const startValue = this.getTypedDateValue("start").trim();
        const endValue = this.getTypedDateValue("end").trim();
        return (!startValue || startValue.length === 10) && (!endValue || endValue.length === 10);
    }
    previewTypedDateSelection() {
        if (!this.canPreviewTypedDates()) {
            return;
        }
        const typedDates = this.getTypedDateRange(false);
        if (typedDates.invalid) {
            return;
        }
        this.setCalenderDates(typedDates.startDate, typedDates.endDate);
    }
    shiftTypedDate(part, days) {
        const value = this.getTypedDateValue(part);
        const date = this.parsePartialTypedDate(value, part === "start") || new Date();
        date.setDate(date.getDate() + days);
        this.setTypedDateValue(part, this.formatTypedDateValue(date));
        this.previewTypedDateSelection();
    }
    focusTypedDateInput(part) {
        const root = this.getPickerRoot();
        const label = part === "start" ? "Start date" : "End date";
        const input = root?.querySelector(`.lp-date-picker_input[aria-label="${label}"]`);
        input?.focus();
        input?.select();
    }
    openTypedDatePicker() {
        const vm = this.getTemplateViewModel();
        if (vm.isShown()) {
            return;
        }
        vm.isShown(true);
        this.bindPickerDismiss();
        requestAnimationFrame(() => {
            this.refreshCalendarRangeClasses();
            this.updatePickerLabels();
        });
    }
    syncTypedDateInputs() {
        const vm = this.getTemplateViewModel();
        vm.typedStartDate(this.formatTypedDateValue(vm.startDate()));
        vm.typedEndDate(this.formatTypedDateValue(vm.endDate()));
        this.syncTypedDateDomInputs();
    }
    apply() {
        this.applyTypedDates(true);
    }
    clearValues(clearCalendars) {
        if (clearCalendars) {
            this.getTemplateViewModel().startDate(null);
            this.getTemplateViewModel().endDate(null);
            this.getTemplateViewModel().selectedMonth(null);
            this.getTemplateViewModel().lastStartDate(null);
            this.getTemplateViewModel().lastEndDate(null);
            this.setCalenderDates(null, null);
        }
        else {
            this.getTemplateViewModel().fromRange(null);
            this.getTemplateViewModel().toRange(null);
        }
    }
    applyFilter() {
        this.setLastToCurrent();
        this.caller.filterDateOnChange(this.idElement);
        this.caller.validationTemplate.clearMessages();
    }
    getAllMonths() {
        let months = [];
        let startDate = new Date();
        let currentYear = startDate.getFullYear();
        let currentMonth = startDate.getMonth() + 2;
        //fill array with dates from the past 12 months
        for (let i = 1; i <= 14; i++) {
            let monthString;
            monthString = currentMonth < 10 ? '0' + currentMonth + '/' + currentYear : currentMonth + '/' + currentYear;
            months.push({ month: monthString, currentYear: currentYear, currentMonth: currentMonth });
            if (currentMonth == 1) {
                currentMonth = 12;
                currentYear -= 1;
            }
            else {
                currentMonth -= 1;
            }
        }
        return months;
    }
    setDateRange(startDate, endDate) {
        //set fiter values
        this.setStartingDate(this.getDateString(startDate, this.getTemplateViewModel().hmsEnabled()));
        this.setEndDate(this.getDateString(endDate, this.getTemplateViewModel().hmsEnabled()));
        //set calender
        this.setCalenderDates(startDate, endDate);
        this.syncTypedDateInputs();
        //show
        this.getTemplateViewModel().isActive(false);
        this.setLastToCurrent();
        return this;
    }
    setStartPickerId(idStartPicker) {
        this.getTemplateViewModel().idStartPicker(idStartPicker);
    }
    toggleClearButton(value) {
        this.getTemplateViewModel().showClearDatesButton(value);
    }
    setEndPickerId(idEndPicker) {
        this.getTemplateViewModel().idEndPicker(idEndPicker);
    }
    setLabel(label) {
        this.getTemplateViewModel().label(label);
    }
    setStartingDate(startDate) {
        this.getTemplateViewModel().startDate(startDate);
        this.syncTypedDateInputs();
    }
    setEndDate(endDate) {
        this.getTemplateViewModel().endDate(endDate);
        this.syncTypedDateInputs();
    }
    getDateString(date, showTime) {
        return getDateString(date, showTime);
    }
    formatTypedDateValue(value) {
        return formatTypedDateValue(value);
    }
    reset() {
        this.getTemplateViewModel().startDate(null);
        this.getTemplateViewModel().endDate(null);
        this.getTemplateViewModel().fromRange(null);
        this.getTemplateViewModel().toRange(null);
        this.getTemplateViewModel().selectedMonth(null);
        this.getTemplateViewModel().typedStartDate("");
        this.getTemplateViewModel().typedEndDate("");
        this.setLastToCurrent();
        this.setCalenderDates(null, null);
        this.getTemplateViewModel().isActive(true);
    }
    onPickerClear() {
        const vm = this.getTemplateViewModel();
        vm.fromRange(null);
        vm.toRange(null);
        vm.typedStartDate("");
        vm.typedEndDate("");
        requestAnimationFrame(() => {
            this.refreshCalendarRangeClasses();
            this.updatePickerLabels();
        });
    }
    onClickClear() {
        this.onClickCancel();
        this.reset();
        this.applyFilter();
    }
    onClickCancel() {
        //restore to previous
        this.getTemplateViewModel().selectedMonth(this.getTemplateViewModel().lastSelectedMonth());
        this.getTemplateViewModel().selectedFilter(this.getTemplateViewModel().lastSelectedFilter());
        this.getTemplateViewModel().startDate(this.getTemplateViewModel().lastStartDate());
        this.getTemplateViewModel().endDate(this.getTemplateViewModel().lastEndDate());
        this.getTemplateViewModel().fromRange(this.getTemplateViewModel().lastFromRange());
        this.getTemplateViewModel().toRange(this.getTemplateViewModel().lastToRange());
        this.syncTypedDateInputs();
        this.setCalenderDates(null, null);
        if (this.getTemplateViewModel().startDate() || this.getTemplateViewModel().endDate() || this.getTemplateViewModel().fromRange() || this.getTemplateViewModel().toRange()) {
            this.setCalenderDates(this.getTemplateViewModel().startDate(), this.getTemplateViewModel().endDate());
            this.getTemplateViewModel().isActive(false);
        }
        this.getTemplateViewModel().isShown(false);
        this.unbindPickerDismiss();
        requestAnimationFrame(() => this.refreshCalendarRangeClasses());
    }
    onClickEdit() {
        const vm = this.getTemplateViewModel();
        vm.isActive(true);
        vm.isShown(true);
        this.syncTypedDateInputs();
        this.setCalenderDates(vm.startDate(), vm.endDate());
        this.bindPickerDismiss();
        requestAnimationFrame(() => {
            this.refreshCalendarRangeClasses();
            this.updatePickerLabels();
        });
    }
    showErrorMsg() {
        //show validartion msg using vnModule_knockoutjs validation template
        let message = { field: "Date Range", label: "Date Range", message: "Invalid filter inputs. Please ensure the dates or ranges are valid, and the start date is on or before the end date" };
        this.caller.validationTemplate.setSingleMessage(message);
    }
    refreshCalendarRangeClasses() { }
    updatePickerLabels() {
        const vm = this.getTemplateViewModel();
        const typedDates = this.getTypedDateRange(false);
        const startDate = vm.isShown()
            ? typedDates.startDate
            : this.normalizeDateOnly(vm.startDate());
        const endDate = vm.isShown()
            ? typedDates.endDate
            : this.normalizeDateOnly(vm.endDate());
        vm.pickerStartLabel(startDate ? this.formatPickerLabel(startDate) : "—");
        vm.pickerEndLabel(endDate ? this.formatPickerLabel(endDate) : "—");
    }
    formatPickerLabel(date) {
        return formatPickerLabel(date);
    }
    normalizeDateOnly(value) {
        return normalizeDateOnly(value);
    }
    isSameCalendarDay(dateA, dateB) {
        return isSameCalendarDay(dateA, dateB);
    }
    setLastToCurrent() {
        this.getTemplateViewModel().lastSelectedFilter(this.getTemplateViewModel().selectedFilter());
        this.getTemplateViewModel().lastStartDate(this.getTemplateViewModel().startDate());
        this.getTemplateViewModel().lastEndDate(this.getTemplateViewModel().endDate());
        this.getTemplateViewModel().lastFromRange(this.getTemplateViewModel().fromRange());
        this.getTemplateViewModel().lastToRange(this.getTemplateViewModel().toRange());
    }
}
